import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DsAccordionModule,
  DsButtonModule, DsEmptyStateModule,
  DsFormFieldModule,
  DsIconModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
  DsModalModule,
  DsTableModule,
  DsTabsModule,
  DsToastModel,
  DsToastService,
} from '@bmw-ds/components';

import { FormsModule } from '@angular/forms';
import { DealerBiddingService } from '../../services/bidding.service';
import { User } from '../../models/user.model';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    DsModalModule,
    DsButtonModule,
    DsIconModule,
    DsAccordionModule,
    DsTableModule,
    DsFormFieldModule,
    FormsModule,
    DsTabsModule,
    DsEmptyStateModule,
  ],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UsersComponent implements OnInit {
  loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };
  canManage = false;

  private readonly loadingAreaService = inject(DsLoadingAreaService);
  private toastService = inject(DsToastService);
  private biddingService = inject(DealerBiddingService);
  private roleService = inject(RoleService);

  addToast(tone: 'critical' | 'positive' | 'neutral', teastText: string): void {
    this.toastService.pushToast({
      id: 'error-toast',
      tone: tone,
      toastText: teastText,
    } as DsToastModel);
  }
  ngOnInit() {
    this.canManage = this.roleService.canManage();
    if(!this.canManage){
      this.addToast('critical', 'You do not have permission to manage users');
      this.onActiveUsersTabSelect();
    }

    if (localStorage.getItem('dealerCode')) {
      const request = {
        dealerCode: localStorage.getItem('dealerCode') || '',
        accessGranted: false,
      };
      this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
      this.biddingService.getDealerUsers(request).subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Error fetching users.'
            );
            this.loadingAreaService.removeLoadingArea(
              this.loadingAreaConfig.id
            );
            return;
          }
          this.usersWaitingForAccess = response.data ?? [];
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        },
        error: (err) => {
          this.addToast('critical', 'Error fetching users: ' + err.message);
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        },
      });
    }
  }

  usersWaitingForAccess: User[] = [];

  activeUsersAtAgency = [
    {
      name: '',
      surname: '',
      email: '',
      role: '',
    },
  ];
  activeTab = 'waiting';

  selectTab(tabId: string) {
    if (this.activeTab === 'waiting') {
      this.ngOnInit();
    }
    this.activeTab = tabId;
  }
  onApprove(user: User) {
    const request = {
      cNumber: user.userId,
      dealerCode: user.dealerCode || localStorage.getItem('dealerCode') || '',
      accessGranted: true,
    };
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.biddingService.grantUserAccess(request).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Error granting access.'
          );
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          return;
        }
        this.addToast('positive', 'User access granted');
        this.ngOnInit(); // Refresh waiting users
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
      error: (err) => {
        this.addToast('critical', 'Error granting access: ' + err.message);
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
    });
  }

  onReject(user: User) {
    const request = {
      cNumber: user.userId,
      dealerCode: user.dealerCode || localStorage.getItem('dealerCode') || '',
    };
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.biddingService.deleteUser(request).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Error deleting user.'
          );
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          return;
        }
        this.addToast('positive', 'User deleted successfully');
        this.ngOnInit(); // Refresh waiting users
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
      error: (err) => {
        this.addToast('critical', 'Error deleting user: ' + err.message);
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
    });
  }

  onActiveUsersTabSelect() {
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    if (localStorage.getItem('dealerCode')) {
      const request = {
        dealerCode: localStorage.getItem('dealerCode') || '',
        accessGranted: true,
      };
      this.biddingService.getDealerUsers(request).subscribe({
        next: (response) => {
          if (!response.success) {
            this.addToast(
              'critical',
              response.error?.message || 'Error fetching users.'
            );
            this.loadingAreaService.removeLoadingArea(
              this.loadingAreaConfig.id
            );
            return;
          }
          this.activeUsersAtAgency = response.data ?? [];
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        },
        error: (err) => {
          this.addToast('critical', 'Error fetching users: ' + err.message);
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        },
      });
    }
  }
}
